/**
 * @fileoverview Sales Pipeline's Pending Approval tab.
 * @description Lists pending prospect review requests from the backend (GET /approvals). A
 * reviewer sees the requests routed to them (`context=REVIEW`) and can approve or reject each from
 * its row's menu (ApproveProspectDialog, RejectProspectDialog); a Sales Executive, who submits
 * prospects for review rather than reviewing them, sees their own submissions
 * (`context=SUBMITTED`) and can withdraw one (WithdrawProspectDialog). Clicking a row opens the
 * prospect read-only. `initialProspectId` — the `prospect` query parameter of the link in the
 * reviewer's email — opens that prospect straight away: into approval for a reviewer, read-only
 * otherwise. Each dialog is mounted only while it's open, so it always starts clean.
 *
 * Above the table: the Pending Review Summary tiles (PendingApprovalsSummary; Super Admin and Sales
 * Head), a search box (bank, city or state) and a lead-rating filter. A reviewer who may decide can
 * also tick rows (up to 50) and approve them together (BulkApproveDialog, Bulk Approve). The ticks
 * are pruned against the rows now showing (useRowSelection), and the dialog gets the rows as they
 * were when it opened, so a refetch cannot change what is approved.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BULK_APPROVE_MAX_REQUESTS } from '@/features/sales/constants/bulk-approve.constants'
import { useApprovalRequests } from '@/features/sales/hooks/useApprovalRequests'
import { useRowSelection } from '@/features/sales/hooks/useRowSelection'
import { approvalContextFor } from '@/features/sales/lib/approval-context.utils'
import { isResultCapExceeded } from '@/features/sales/lib/result-cap.utils'
import Button from '@/shared/components/atoms/Button'
import Checkbox from '@/shared/components/atoms/Checkbox'
import Select from '@/shared/components/atoms/Select'
import RowActionsMenu from '@/shared/components/molecules/RowActionsMenu'
import { useDebouncedValue } from '@/shared/hooks/useDebouncedValue'

import ApproveProspectDialog from './ApproveProspectDialog'
import BulkApproveDialog from './BulkApproveDialog'
import PendingApprovalsSummary from './PendingApprovalsSummary'
import ProspectDetailModal from './ProspectDetailModal'
import RejectProspectDialog from './RejectProspectDialog'
import TableEmptyState from './TableEmptyState'
import TableSearchInput from './TableSearchInput'
import TableSkeleton from './TableSkeleton'
import WithdrawProspectDialog from './WithdrawProspectDialog'

import type { ApprovalListItem, BulkApproveResult } from '@/features/sales/types/approval.types'
import type { ProspectLeadRating } from '@/features/sales/types/prospect.types'
import type { RowAction } from '@/shared/components/molecules/RowActionsMenu'
import type { ChangeEvent } from 'react'

const GRID = 'grid-cols-[34px_2fr_1.2fr_0.7fr_1.2fr_1.2fr_1.2fr_1fr_0.5fr]'

const LEAD_RATINGS: ProspectLeadRating[] = ['HOT', 'WARM', 'COLD']

type Decision = 'approve' | 'reject' | 'withdraw'

/**
 * @description A request's id.
 * @param {ApprovalListItem} request - The request.
 * @returns {string} Its approval request id.
 */
const approvalRequestIdOf = (request: ApprovalListItem) => request.approvalRequestId

interface PendingApprovalsPanelProps {
  viewerRole: string
  /** A prospect to open as soon as it's loaded, e.g. from the reviewer's email link. */
  initialProspectId?: string | null
  /** Called with a flash message once a request is approved, rejected or withdrawn. */
  onDecided: (message: string) => void
  /** Whether the viewer may read the Pending Review Summary (Super Admin, Sales Head). */
  canSeeSummary?: boolean
  /** Whether the viewer holds the approval-decision capability (enables Bulk Approve). */
  canDecide?: boolean
}

interface PendingRowProps {
  request: ApprovalListItem
  actions: RowAction[]
  onView: () => void
  /** The row's checkbox, when the viewer can bulk-approve. */
  selection?: { selected: boolean; onToggle: () => void } | undefined
}

/**
 * @description The Pending Approval table's column headers.
 */
function PendingHeader() {
  const { t } = useTranslation()

  return (
    <div className={`grid ${GRID} items-center gap-3 bg-input-bg px-4 py-2.5 sm:px-5`}>
      <span />
      {[
        t('sales.pipeline.columns.bankTenantId'),
        t('sales.pipeline.columns.location'),
        t('sales.pipeline.columns.rating'),
        t('sales.pipeline.pending.columns.submittedBy'),
        t('sales.pipeline.pending.columns.assignee'),
        t('sales.pipeline.pending.columns.approver'),
        t('sales.pipeline.pending.columns.submittedOn'),
        t('sales.pipeline.columns.actions'),
      ].map((label, index, all) => (
        <span
          key={label}
          className={`text-[11px] font-semibold tracking-[0.6px] text-table-header ${
            index === all.length - 1 ? 'text-right' : ''
          }`}
        >
          {label}
        </span>
      ))}
    </div>
  )
}

/**
 * @description One pending request's row: who submitted it, for whom, and who reviews it.
 * Clicking the row (or the bank name, for keyboard users) opens the prospect read-only.
 * @param {PendingRowProps} props - The request, its menu, and the view handler.
 * @param {ApprovalListItem} props.request - The pending request.
 * @param {RowAction[]} props.actions - The row's menu items.
 * @param {() => void} props.onView - Opens the prospect read-only.
 * @param {{ selected: boolean; onToggle: () => void }} [props.selection] - The row's checkbox.
 */
function PendingRow({ request, actions, onView, selection }: PendingRowProps) {
  const { t } = useTranslation()

  return (
    <div
      onClick={onView}
      data-testid="pending-approval-row"
      className={`grid ${GRID} cursor-pointer items-center gap-3 border-b border-row-border px-4 py-2.5 hover:bg-surface-hover sm:px-5`}
    >
      <span onClick={(event) => event.stopPropagation()} className="flex">
        {selection && (
          <Checkbox
            checked={selection.selected}
            onChange={selection.onToggle}
            aria-label={request.bankName}
            data-testid="pending-approval-select-checkbox"
          />
        )}
      </span>
      <div className="flex min-w-0 flex-col">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onView()
          }}
          data-testid="pending-approval-name-button"
          className="min-w-0 cursor-pointer truncate text-left text-[13.5px] font-semibold text-primary hover:underline"
        >
          {request.bankName}
        </button>
        <span className="truncate text-xs text-footer" data-testid="pending-approval-tenant-code">
          {request.tenantCode}
        </span>
      </div>
      <span className="truncate text-[13px] text-cell" data-testid="pending-approval-location">
        {request.city}, {request.state}
      </span>
      <span className="text-[13px] text-cell" data-testid="pending-approval-rating">
        {t(`sales.pipeline.ratings.${request.leadRating}`)}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="pending-approval-submitted-by">
        {request.submittedBy.displayName}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="pending-approval-assignee">
        {request.proposedOwner?.displayName ?? t('sales.pipeline.filters.unassigned')}
      </span>
      <span className="truncate text-[13px] text-cell" data-testid="pending-approval-approver">
        {request.approver.displayName}
      </span>
      <span className="text-[13px] text-cell" data-testid="pending-approval-submitted-on">
        {request.submittedAt.slice(0, 10)}
      </span>
      <RowActionsMenu
        label={t('sales.pipeline.rowActions', { name: request.bankName })}
        actions={actions}
      />
    </div>
  )
}

/**
 * @component
 * @param {PendingApprovalsPanelProps} props - The viewer, the deep link, and the handlers.
 * @param {string} props.viewerRole - The viewer's active role name.
 * @param {string | null} [props.initialProspectId] - A prospect to open straight away.
 * @param {(message: string) => void} props.onDecided - Called with a flash message after a decision.
 * @param {boolean} [props.canSeeSummary] - Whether to show the Pending Review Summary tiles.
 * @param {boolean} [props.canDecide] - Whether the viewer may decide requests (enables Bulk Approve).
 */
function PendingApprovalsPanel({
  viewerRole,
  initialProspectId,
  onDecided,
  canSeeSummary = false,
  canDecide = false,
}: PendingApprovalsPanelProps) {
  const { t } = useTranslation()
  const context = approvalContextFor(viewerRole)
  const [search, setSearch] = useState('')
  const [leadRating, setLeadRating] = useState<ProspectLeadRating | ''>('')
  const debouncedSearch = useDebouncedValue(search)
  const requestsQuery = useApprovalRequests({
    context,
    status: 'PENDING',
    ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
    ...(leadRating && { leadRating }),
  })
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [deciding, setDeciding] = useState<{
    request: ApprovalListItem
    decision: Decision
  } | null>(null)
  const [bulkBatch, setBulkBatch] = useState<ApprovalListItem[] | null>(null)
  const [deepLinkDone, setDeepLinkDone] = useState(false)
  const requests = requestsQuery.data ?? []
  const canBulk = canDecide && context === 'REVIEW'
  const selection = useRowSelection(requests, approvalRequestIdOf, BULK_APPROVE_MAX_REQUESTS)

  // Open the linked prospect once the list has loaded — during render, React's "adjust state when
  // data arrives" pattern, rather than in an effect.
  if (!deepLinkDone && requestsQuery.isSuccess && initialProspectId) {
    setDeepLinkDone(true)
    const linked = requests.find((request) => request.prospectId === initialProspectId)
    if (linked && context === 'REVIEW') setDeciding({ request: linked, decision: 'approve' })
    else if (linked) setViewingId(linked.prospectId)
  }

  /**
   * @description A pending row's menu: View, then Approve and Reject for a reviewer, or Withdraw
   * for the submitter.
   * @param {ApprovalListItem} request - The row's request.
   * @returns {RowAction[]} The menu items.
   */
  const rowActions = (request: ApprovalListItem): RowAction[] => {
    const decisions: Decision[] = context === 'REVIEW' ? ['approve', 'reject'] : ['withdraw']
    return [
      {
        key: 'view',
        label: t('sales.activeClients.view'),
        onSelect: () => setViewingId(request.prospectId),
      },
      ...decisions.map((decision) => ({
        key: decision,
        label: t(`sales.pipeline.pending.${decision}`),
        onSelect: () => setDeciding({ request, decision }),
        ...(decision === 'approve' ? {} : { tone: 'danger' as const }),
      })),
    ]
  }

  /**
   * @description Closes the open dialog and reports a decision with its flash message.
   * @param {string} key - The flash message's translation key.
   * @param {ApprovalListItem} request - The decided request.
   */
  const decided = (key: string, request: ApprovalListItem) => {
    setDeciding(null)
    onDecided(t(key, { bank: request.bankName }))
  }

  /**
   * @description Opens Bulk Approve for the rows ticked right now (a snapshot of them).
   */
  const openBulk = () => setBulkBatch(selection.selectedRows)

  /**
   * @description Closes Bulk Approve without reporting anything.
   */
  const closeBulk = () => setBulkBatch(null)

  /**
   * @description Closes whichever approve, reject or withdraw dialog is open.
   */
  const closeDecision = () => setDeciding(null)

  /**
   * @description Changes the search text and clears the ticks.
   * @param {string} value - The new search text.
   */
  const changeSearch = (value: string) => {
    setSearch(value)
    selection.clear()
  }

  /**
   * @description Changes the lead-rating filter and clears the ticks.
   * @param {ChangeEvent<HTMLSelectElement>} event - The select's change event.
   */
  const changeRating = (event: ChangeEvent<HTMLSelectElement>) => {
    setLeadRating(event.target.value as ProspectLeadRating | '')
    selection.clear()
  }

  /**
   * @description Reports a finished Bulk Approve and clears the ticks.
   * @param {BulkApproveResult} result - What the batch answered.
   */
  const onBulkDone = (result: BulkApproveResult) => {
    setBulkBatch(null)
    selection.clear()
    onDecided(
      t('sales.pipeline.flash.bulkApproved', {
        approved: result.approved.length,
        skipped: result.skipped.length,
      }),
    )
  }

  return (
    <>
      <PendingApprovalsSummary enabled={canSeeSummary} />
      <div className="flex flex-wrap items-center gap-2.5 px-3.5 pb-3.5 sm:px-4">
        <TableSearchInput
          value={search}
          onChange={changeSearch}
          placeholder={t('sales.pipeline.pending.searchPlaceholder')}
          data-testid="pending-approvals-search-input"
        />
        <Select
          aria-label={t('sales.pipeline.columns.rating')}
          data-testid="pending-approvals-rating-select"
          value={leadRating}
          onChange={changeRating}
          options={[
            { value: '', label: t('sales.pipeline.filters.allRatings') },
            ...LEAD_RATINGS.map((rating) => ({
              value: rating,
              label: t(`sales.pipeline.ratings.${rating}`),
            })),
          ]}
        />
      </div>
      {canBulk && selection.count > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 border-y border-selection-border bg-selection-bg px-3.5 py-2.5 sm:px-4"
          data-testid="pending-approvals-selection-bar"
        >
          <span className="text-[13px] font-bold text-info">
            {t('sales.pipeline.selectionLine', { count: selection.count })}
          </span>
          {selection.isFull && (
            <span className="text-xs text-slate" data-testid="pending-approvals-selection-limit">
              {t('sales.pipeline.bulkApprove.selectionLimit', { max: BULK_APPROVE_MAX_REQUESTS })}
            </span>
          )}
          <button
            type="button"
            onClick={selection.clear}
            data-testid="pending-approvals-clear-selection-button"
            className="cursor-pointer text-xs font-semibold text-link-muted underline"
          >
            {t('sales.pipeline.clearSelection')}
          </button>
          <span className="flex-1" />
          <div className="w-fit">
            <Button
              type="button"
              size="md"
              onClick={openBulk}
              data-testid="pending-approvals-bulk-approve-button"
            >
              {t('sales.pipeline.bulkApprove.action')}
            </Button>
          </div>
        </div>
      )}
      {requestsQuery.isPending && <TableSkeleton columns={9} />}
      {requestsQuery.isError && (
        <p className="p-4 text-sm text-danger" data-testid="pending-approvals-load-error">
          {isResultCapExceeded(requestsQuery.error)
            ? t('sales.pipeline.tooManyResults')
            : t('sales.pipeline.loadError')}
        </p>
      )}
      {requestsQuery.isSuccess && (
        <div
          className="overflow-x-auto border-t border-divider"
          data-testid="pending-approvals-table"
        >
          <div className="min-w-[1200px]">
            <PendingHeader />
            {requests.map((request) => (
              <PendingRow
                key={request.approvalRequestId}
                request={request}
                actions={rowActions(request)}
                onView={() => setViewingId(request.prospectId)}
                selection={
                  canBulk
                    ? {
                        selected: selection.isSelected(request.approvalRequestId),
                        onToggle: () => selection.toggle(request.approvalRequestId),
                      }
                    : undefined
                }
              />
            ))}
            {requests.length === 0 && (
              <TableEmptyState
                message={t('sales.pipeline.pending.empty')}
                description={t('sales.pipeline.pending.emptyDescription')}
                icon="check"
                tone="good"
              />
            )}
          </div>
        </div>
      )}
      <ProspectDetailModal prospectId={viewingId} onClose={() => setViewingId(null)} />
      {bulkBatch && bulkBatch.length > 0 && (
        <BulkApproveDialog requests={bulkBatch} onClose={closeBulk} onDone={onBulkDone} />
      )}
      {deciding?.decision === 'approve' && (
        <ApproveProspectDialog
          request={deciding.request}
          onClose={closeDecision}
          onApproved={(request) => decided('sales.pipeline.flash.approved', request)}
        />
      )}
      {deciding?.decision === 'reject' && (
        <RejectProspectDialog
          request={deciding.request}
          onClose={closeDecision}
          onRejected={(request) => decided('sales.pipeline.flash.rejected', request)}
        />
      )}
      {deciding?.decision === 'withdraw' && (
        <WithdrawProspectDialog
          request={deciding.request}
          onClose={closeDecision}
          onWithdrawn={(request) => decided('sales.pipeline.flash.withdrawn', request)}
        />
      )}
    </>
  )
}

export default PendingApprovalsPanel
